import { AnyAction } from 'redux';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, LoadOptions } from 'app/types-shared';
import { STATE } from 'app/constants';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { isForbiddenStatus, blockAccess } from 'app/area/shared/actions-utils';
import { subscriberGroupsTranslation } from '../constants';
import { defaultMaxResults } from './constants';
import { sharedApi } from '../shared/api';
import {
  ClearList, RequestList, ReceiveList, FailList,
  UpdateValidation,
  UpdateMergeSubscriberGroup,
  RequestSubscriberGroupOptions,
  ResetValidation,
  ReceiveSubscriberGroupOptions,
  RequestSubscriberGroupItemDelete,
  ReceiveSubscriberGroupItemDelete,
  FailSubscriberGroupItemDelete,
  FailSubscriberGroupOptions
} from './action-types';
import { api } from './api';
import { mapList } from './map-list';
import { SubscriberGroupsSearchModel } from './types-store';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import {
  DeleteSubscriberGroupItemPayload, MergeSubscriberGroupState
} from 'app/area/subscriber-groups/shared/merge-subscriber-group/types-store';
import { subscriberGroupOptionsMapped } from 'app/area/subscriber-groups/shared/merge-subscriber-group/mapping';
import { ValidationProps } from 'ui';

export const actionCreators = {

  loadSubscriberGroupList: (
    model: SubscriberGroupsSearchModel,
    startIndex: number = 0,
    maxResults: number = defaultMaxResults
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const requestId = Date.now();
      dispatch(new RequestList(requestId, model, startIndex === 0));

      try {

        const listResponse = await api.loadSubscriberGroupList(model, startIndex, maxResults);
        dispatch(new ReceiveList(requestId, mapList(listResponse.results), listResponse.totalNumberOfResults));

      } catch (err) {
        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.subscriberGroups,
            translationId: subscriberGroupsTranslation.no_access_to_subscriber_groups
          });
        }

        dispatch(new FailList(requestId));
      }
    },

  updateMergeSubscriberGroup: (mergeSubscriberGroup: MergeSubscriberGroupState): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new UpdateMergeSubscriberGroup(mergeSubscriberGroup));
    },

  loadSubscriberGroupOptions: (loadOptionsParams: LoadOptions): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestSubscriberGroupOptions());

      try {
        const subscriberGroupOptions = await sharedApi.loadSubscriberGroupOptions(loadOptionsParams);
        dispatch(new ReceiveSubscriberGroupOptions(subscriberGroupOptionsMapped(subscriberGroupOptions.data.results)));
      } catch (err) {
        dispatch(new FailSubscriberGroupOptions());
      }
    },

  deleteMergeSubscriberGroup: (payload: DeleteSubscriberGroupItemPayload): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const { id, successMessage, reassignReferencesToCode } = payload;
      dispatch(new RequestSubscriberGroupItemDelete(id));

      try {
        await sharedApi.deleteMergeSubscriberGroup(id, reassignReferencesToCode);

        dispatch(new ReceiveSubscriberGroupItemDelete(id));
        dispatch(new UpdateMergeSubscriberGroup(undefined));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        dispatch(new FailSubscriberGroupItemDelete(id));

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }
      }
    },

  resetValidation: (key: string): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(key));
  },

  updateValidation: (validation: { [key: string]: ValidationProps }): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateValidation(validation));
  },

  clearList: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      const requestId = Date.now();
      dispatch(new ClearList(requestId));
    },

  displayError: (errorMessage: string): ActionDispatching<any, any> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
